import { Link } from 'react-router-dom';
import type { Task } from '../lib/types';
import Markdown from './Markdown';

interface Props {
  /** Direct parent task (closest ancestor). */
  parent: Task;
  /** Older ancestors, oldest-first. Compact pills rendered as breadcrumbs. */
  olderAncestors?: Task[];
  projectSlug: string;
}

/**
 * Shown above the prompt on any sub-task's detail page. Surfaces the
 * pipeline-generated parent summary so the operator doesn't have to
 * open the parent task to remember what it accomplished — the same
 * summary the pipeline already injects into agent memory via
 * `renderParentTaskContext`, just exposed to humans too.
 *
 * When the parent itself is a sub-task (i.e. there are grandparents),
 * we show a small breadcrumb above the summary so the chain is visible
 * at a glance. The full chain navigator (TaskChain) lives elsewhere on
 * the page; this is the focused parent-context view.
 */
export default function ParentSummaryBanner({
  parent,
  olderAncestors,
  projectSlug,
}: Props) {
  const hasOlder = olderAncestors && olderAncestors.length > 0;

  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 mb-4">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <span className="text-[10px] uppercase tracking-wide text-sky-700 font-semibold">
          Following up on
        </span>
        <Link
          to={`/p/${projectSlug}/tasks/${parent.id}`}
          className="text-sm font-semibold text-sky-900 hover:underline truncate max-w-[400px]"
          title={parent.title}
        >
          {parent.title}
        </Link>
        <span className="font-mono text-[10px] text-sky-600 opacity-70">
          {parent.id.slice(0, 8)}
        </span>
      </div>

      {hasOlder && (
        <div className="flex items-center gap-1 text-[11px] text-sky-700 mb-2 flex-wrap">
          <span className="opacity-70">earlier:</span>
          {olderAncestors!.map((a, i) => (
            <span key={a.id} className="inline-flex items-center gap-1">
              <Link
                to={`/p/${projectSlug}/tasks/${a.id}`}
                className="rounded bg-sky-100 px-1.5 py-0.5 hover:bg-sky-200 max-w-[180px] truncate inline-block"
                title={a.title}
              >
                {a.title}
              </Link>
              {i < olderAncestors!.length - 1 && (
                <span className="opacity-50">→</span>
              )}
            </span>
          ))}
          <span className="opacity-50">→</span>
          <span className="opacity-70">(parent)</span>
        </div>
      )}

      {parent.task_summary ? (
        <div className="prose prose-sm prose-sky max-w-none text-sky-950">
          <Markdown source={parent.task_summary} />
        </div>
      ) : (
        <p className="text-sm text-sky-800 italic">
          {parent.prompt.length > 200
            ? `${parent.prompt.slice(0, 200)}…`
            : parent.prompt}
          <br />
          <span className="text-xs opacity-70">
            (Parent summary not yet available — the pipeline writes it at task
            completion.)
          </span>
        </p>
      )}
    </div>
  );
}
